/**
 * Spells drawn from the effects library (games-media `bench fx`, the FX_ART sets): shield, rock and ice wall faces
 * sheared onto each chord of a wall, mines arming and pulsing, storm columns stood along a storm's line, the lava
 * pool, status overlays on bodies, and one-shot bursts (impacts, blasts, nova fronts, walls breaking) from a small
 * pool. Every draw returns false when its set has not landed (or lacks the clip) and the caller draws the old
 * vector stand-in. Visual only: when a hazard appeared or vanished is read from what the snapshots show.
 */

import { BARRIER, type DamageType, type Element, type Segment } from '@shared/index.js';
import type { ViewHazard } from '../state/viewModel.js';
import { clamp01, UNIT, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';
import type { FxSet } from './spriteSets.js';
import { art } from './sprites.js';

/** A projector that leaves screen coordinates alone, so a set draws where the caller already is. */
const SCREEN = { sx: (v: number): number => v, sy: (v: number): number => v };

/** One clip clock id per (set, clip): every draw passes `at`, so the clock only needs to stay put. */
const clipIds = new Map<string, number>();
function clipId(set: string, clip: string): number {
  const key = `${set}/${clip}`;
  let id = clipIds.get(key);
  if (id === undefined) {
    id = -1000 - clipIds.size;
    clipIds.set(key, id);
  }
  return id;
}

/** True when the set has landed and has the clip. */
export function fxHas(set: FxSet, clip: string): boolean {
  return art.set(set)?.has(clip) ?? false;
}

/** How long a one-shot clip runs (0 when the set or clip is missing). */
export function fxSeconds(set: FxSet, clip: string): number {
  return art.set(set)?.seconds(clip) ?? 0;
}

/**
 * Draw `clip` of an effect set `at` seconds in, its anchor at screen (x, y); `flip` mirrors it, `scale` sizes it
 * about the anchor. False when the set or clip is not there.
 */
export function fxAt(ctx: Ctx, set: FxSet, clip: string, at: number, x: number, y: number, alpha = 1, flip = false, scale = 1): boolean {
  const s = art.set(set);
  if (!s || !s.has(clip)) return false;
  if (!flip && scale === 1) {
    s.draw(ctx, SCREEN, clipId(set, clip), clip, 0, 0, x, y, { at, alpha });
    return true;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(flip ? -scale : scale, scale);
  s.draw(ctx, SCREEN, clipId(set, clip), clip, 0, 0, 0, 0, { at, alpha });
  ctx.restore();
  return true;
}

/** The effect element for a kind of damage (physical throws dust and pebbles). */
export function damageElement(t: DamageType): Element {
  return t === 'physical' ? 'earth' : t;
}

/** Woven pieces have no effects of their own yet: each borrows its nearest parent's (Plasma lightning, Ash fire...). */
const WOVEN_FX: Partial<Record<Element, Element>> = {
  plasma: 'lightning', flux: 'arcane', stillness: 'cold', obsidian: 'earth', ether: 'arcane', cloud: 'water', ash: 'fire', rime: 'cold',
  moment: 'arcane', dragonfire: 'fire', stormglass: 'ice', thunderhead: 'lightning', time: 'arcane',
  tide: 'water', pulse: 'life', void: 'arcane', wildfire: 'fire', whirlpool: 'water', deluge: 'water', inferno: 'lava', glacier: 'ice',
  soul: 'life', sun: 'fire', winter: 'cold', death: 'arcane',
};

/** The element whose effects draw `e` (itself, or a woven piece's nearest parent). */
export function fxElement(e: Element): Element {
  return WOVEN_FX[e] ?? e;
}

// ------------------------------------------------------------------ hazard lifetimes

/** What a hazard looked like when last seen (kept per id, refreshed in place). */
export interface HazardSeen { id: number; k: ViewHazard['k']; rx: number; ry: number; geo: ViewHazard['geo']; ang: number; r: number; el: readonly Element[]; hp: number; t: number }
interface Life { born: number; frame: number; last: HazardSeen }

const lives = new Map<number, Life>();
let lifeFrame = 0;
let primed = false;

/**
 * Once a frame: note new hazards (their rise and arming play from now) and hand every hazard that vanished since
 * the last frame to `onGone`, as last seen. Hazards present on the first call (a join mid-match) count as old.
 */
export function trackHazards(hazards: readonly ViewHazard[], time: number, onGone: (h: HazardSeen) => void): void {
  lifeFrame++;
  for (const h of hazards) {
    let l = lives.get(h.id);
    if (!l) {
      l = { born: primed ? time : -1e9, frame: lifeFrame, last: { id: h.id, k: h.k, rx: 0, ry: 0, geo: h.geo, ang: 0, r: 0, el: h.el, hp: 0, t: 0 } };
      lives.set(h.id, l);
    }
    l.frame = lifeFrame;
    const s = l.last;
    s.k = h.k; s.rx = h.rx; s.ry = h.ry; s.geo = h.geo; s.ang = h.ang; s.r = h.r; s.el = h.el; s.hp = h.hp; s.t = h.t;
  }
  primed = true;
  for (const [id, l] of lives) {
    if (l.frame === lifeFrame) continue;
    lives.delete(id);
    onGone(l.last);
  }
}

/** Seconds since a hazard was first seen (large for ones that were already there). */
export function hazardAge(id: number, time: number): number {
  const l = lives.get(id);
  return l ? time - l.born : 1e9;
}

/** Forget every hazard without breaking any (a new room). */
export function resetHazards(): void {
  lives.clear();
  primed = false;
}

// ------------------------------------------------------------------ walls and shields

/** Height of each face texture in px (rock and ice include the cap and the crystal tips). */
const FACE_H: Record<'fx-shield-wall' | 'fx-rock-wall' | 'fx-ice-wall', number> = { 'fx-shield-wall': 26, 'fx-rock-wall': 30, 'fx-ice-wall': 34 };
const FACE_TILE = 32;
const SHIELD_SHEEN = 'rgba(245, 230, 99, 0.2)';
/** Rock and ice push up out of the ground over this long. */
const WALL_RISE_S = 0.25;

export function wallSet(h: { k: ViewHazard['k']; el: readonly Element[] }): 'fx-shield-wall' | 'fx-rock-wall' | 'fx-ice-wall' {
  return h.k === 'barrier' ? 'fx-shield-wall' : h.el.includes('ice') ? 'fx-ice-wall' : 'fx-rock-wall';
}

/** A wall's hp out of its full (the numbers the hp hint uses). */
export function wallHpFrac(h: { k: ViewHazard['k']; el: readonly Element[]; hp: number }): number {
  const max = h.k === 'barrier' ? BARRIER.hp : h.el.includes('ice') ? 1000 : 2500;
  return clamp01(h.hp / max);
}

/**
 * One chord of a shield or wall as its face texture, tiled along the chord and sheared so it stands straight up
 * from the ground line (the same parallelogram the vector slab filled). Shields rise from the ground in their own
 * clip and crack below a third of their hp; rock and ice push up out of the floor and crack in two stages.
 */
export function drawWallFace(ctx: Ctx, cam: Camera, h: ViewHazard, seg: Segment, index: number, time: number): boolean {
  const name = wallSet(h);
  const s = art.set(name);
  if (!s) return false;
  const age = hazardAge(h.id, time);
  const frac = wallHpFrac(h);
  let clip: string;
  let at = time + index * 0.09;
  let rise = 1;
  if (name === 'fx-shield-wall') {
    const riseS = s.seconds('rise');
    if (age < riseS) { clip = 'rise'; at = age; } else clip = frac < 0.35 ? 'cracked' : 'idle';
  } else {
    rise = clamp01(age / WALL_RISE_S);
    clip = frac > 0.66 ? 'idle' : frac > 0.33 ? 'crack1' : 'crack2';
  }
  if (!s.has(clip)) return false;
  let ax = cam.sx(seg[0].x), ay = cam.sy(seg[0].y), bx = cam.sx(seg[1].x), by = cam.sy(seg[1].y);
  // keep the texture's left on the screen's left, so its light falls the same way on every chord
  if (bx < ax) { const tx = ax, ty = ay; ax = bx; ay = by; bx = tx; by = ty; }
  const dx = bx - ax, dy = by - ay;
  const L = Math.hypot(dx, dy);
  if (L < 0.5) return true;
  const H = FACE_H[name];
  const sink = (1 - rise) * H;
  // a shield thins as it weakens and flickers near the end; walls are solid
  const alpha = name === 'fx-shield-wall' ? (0.55 + 0.4 * frac) * (frac < 0.2 && Math.sin(time * 24) > 0.3 ? 0.6 : 1) : 1;
  ctx.save();
  ctx.translate(ax, ay);
  ctx.transform(dx / L, dy / L, 0, 1, 0, 0);
  ctx.beginPath();
  ctx.rect(0, -H - 2, L, H + 2);
  ctx.clip();
  if (name === 'fx-shield-wall') {
    // a faint gold sheen behind the lattice, so the field reads as light and not a net
    ctx.fillStyle = SHIELD_SHEEN;
    ctx.globalAlpha = alpha * (clip === 'rise' ? 0.5 : 1);
    ctx.fillRect(0, -H + 2, L, H - 2);
    ctx.globalAlpha = 1;
  }
  const phase = (index * 13 + h.id * 5) % FACE_TILE;
  for (let u = -phase; u < L; u += FACE_TILE) s.draw(ctx, SCREEN, clipId(name, clip), clip, 0, 0, u, sink, { at, alpha });
  ctx.restore();
  return true;
}

// ------------------------------------------------------------------ mines, storms, pools

/** A mine's plate: arming for its first moments, then pulsing; each mine on its own beat. */
export function drawMineArt(ctx: Ctx, cam: Camera, h: ViewHazard, payload: Element, time: number): boolean {
  const age = hazardAge(h.id, time);
  const el = fxElement(payload);
  const arm = `arm-${el}`;
  const armS = fxSeconds('fx-mine', arm);
  const px = cam.sx(h.rx), py = cam.sy(h.ry);
  if (age < armS) return fxAt(ctx, 'fx-mine', arm, age, px, py);
  return fxAt(ctx, 'fx-mine', `idle-${el}`, time + h.id * 0.37, px, py);
}

/** The Lava boulder's pool, scaled to its radius (baked at 1.6 units), fading in its last second. */
export function drawPoolArt(ctx: Ctx, cam: Camera, h: ViewHazard, time: number): boolean {
  const scale = (h.r > 0 ? h.r : 1.6) / 1.6;
  return fxAt(ctx, 'fx-pool', 'lava', time + h.id * 0.21, cam.sx(h.rx), cam.sy(h.ry), Math.min(1, h.t), false, scale);
}

/** World units between storm columns along the storm's line. */
const STORM_SPACING = 0.8;
interface Cols { pts: { x: number; y: number }[]; seen: number }
const stormCols = new Map<number, Cols>();

/** Where a storm's columns stand: every STORM_SPACING along its chords (cached; storms never move). */
export function stormColumns(h: ViewHazard, segs: readonly Segment[], time: number): readonly { x: number; y: number }[] {
  const hit = stormCols.get(h.id);
  if (hit) { hit.seen = time; return hit.pts; }
  const pts: { x: number; y: number }[] = [];
  let carry = STORM_SPACING * 0.5;
  for (const [a, b] of segs) {
    const L = Math.hypot(b.x - a.x, b.y - a.y);
    let d = carry;
    while (d < L) {
      const k = d / L;
      pts.push({ x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k });
      d += STORM_SPACING;
    }
    carry = d - L;
  }
  if (pts.length === 0) pts.push({ x: h.rx, y: h.ry });
  stormCols.set(h.id, { pts, seen: time });
  return pts;
}

/** Drop cached columns for storms not seen for 2 s. */
export function sweepStormColumns(time: number): void {
  stormCols.forEach((v, k) => { if (time - v.seen > 2) stormCols.delete(k); });
}

/** The element a storm column shows: the storm's payload elements in turn along its line. */
export function stormElement(h: { el: readonly Element[] }, j: number): Element {
  const els = h.el.filter((e) => e !== 'shield');
  return els.length > 0 ? fxElement(els[j % els.length]!) : 'lightning';
}

/** One storm column, rising in over a third of a second and thinning in the storm's last half second. */
export function drawStormColumn(ctx: Ctx, cam: Camera, h: ViewHazard, j: number, x: number, y: number, time: number): boolean {
  const age = hazardAge(h.id, time);
  const alpha = Math.min(1, age / 0.35, h.t / 0.5 + 0.1);
  if (alpha <= 0.02) return true;
  return fxAt(ctx, 'fx-storm', `storm-${stormElement(h, j)}`, time + j * 0.29, cam.sx(x), cam.sy(y), Math.max(0, alpha), j % 2 === 1);
}

// ------------------------------------------------------------------ bodies: statuses, armour, the bubble

/** Which ice block fits a body `hPx` tall. */
export function frozenSize(hPx: number): 's' | 'm' | 'l' {
  return hPx <= 20 ? 's' : hPx <= 52 ? 'm' : 'l';
}

const frozenSince = new Map<number, number>();

/** The ice block over a frozen body: forming when it has just frozen, then glinting. */
export function drawFrozenArt(ctx: Ctx, id: number, px: number, py: number, hPx: number, thaw: number, time: number): boolean {
  let since = frozenSince.get(id);
  if (since === undefined) { since = time; frozenSince.set(id, since); }
  const size = frozenSize(hPx);
  const age = time - since;
  const form = `frozen-${size}-form`;
  const forming = age < fxSeconds('fx-status', form);
  const clip = forming ? form : `frozen-${size}`;
  const at = forming ? age : time + id * 0.3;
  // the fill see-through so the body shows, the rim and facets crisp on top
  if (!fxAt(ctx, 'fx-status', `${clip}-fill`, at, px, py, ICE_FILL_ALPHA * thaw)) return false;
  return fxAt(ctx, 'fx-status', clip, at, px, py, thaw);
}

/** How much of the body an ice block's fill hides. */
export const ICE_FILL_ALPHA = 0.42;

/** A body that is not frozen forgets when it froze (so the next freeze forms again). */
export function notFrozen(id: number): void {
  if (frozenSince.size > 0) frozenSince.delete(id);
}

/** Forget bodies that are gone (one that died frozen never reaches `notFrozen`). */
export function sweepFrozen(alive: (id: number) => boolean): void {
  frozenSince.forEach((_, id) => { if (!alive(id)) frozenSince.delete(id); });
}

/** Flames for a burning body, one per stack (up to six), in rows up the body. */
export function drawBurnArt(ctx: Ctx, px: number, py: number, r: number, h: number, stacks: number, time: number): boolean {
  if (!fxHas('fx-status', 'burn-a')) return false;
  const n = Math.min(6, stacks);
  for (let i = 0; i < n; i++) {
    const fx = px + ((i % 3) - 1) * r * 0.6;
    const fy = py - h * (0.05 + 0.3 * Math.floor(i / 3)) - (i % 2) * 2;
    fxAt(ctx, 'fx-status', i % 2 ? 'burn-b' : 'burn-a', time + i * 0.13, fx, fy, 1, i % 3 === 2);
  }
  return true;
}

/** An overlay sized for a wizard, repeated up a taller body. */
export function drawBodyOverlay(ctx: Ctx, clip: 'chill' | 'shock', px: number, py: number, hPx: number, time: number): boolean {
  if (!fxHas('fx-status', clip)) return false;
  const reps = hPx > 60 ? 2 : 1;
  for (let k = 0; k < reps; k++) fxAt(ctx, 'fx-status', clip, time + k * 0.41, px, py - k * hPx * 0.45, 1, k === 1);
  return true;
}

// ------------------------------------------------------------------ beams, arcs, sprays, projectiles

/**
 * A strip clip (a beam's or an arc's: anchored at its left end on its centre line) tiled from screen (x0, y0) to
 * (x1, y1), turned to lie along it and `thick` times as wide; `phase` px shifts the tiles so neighbours differ.
 */
export function fxStrip(ctx: Ctx, set: FxSet, clip: string, at: number, x0: number, y0: number, x1: number, y1: number, thick = 1, phase = 0, alpha = 1): boolean {
  const s = art.set(set);
  if (!s || !s.has(clip)) return false;
  const dx = x1 - x0, dy = y1 - y0;
  const L = Math.hypot(dx, dy);
  if (L < 0.5) return true;
  ctx.save();
  ctx.translate(x0, y0);
  ctx.transform(dx / L, dy / L, -dy / L, dx / L, 0, 0);
  ctx.beginPath();
  ctx.rect(0, -12 * thick, L, 24 * thick);
  ctx.clip();
  if (thick !== 1) ctx.scale(1, thick);
  const id = clipId(set, clip);
  for (let u = -(phase % FACE_TILE); u < L; u += FACE_TILE) s.draw(ctx, SCREEN, id, clip, 0, 0, u, 0, { at, alpha });
  ctx.restore();
  return true;
}

/** `clip` drawn at screen (x, y), turned by `ang` radians (an ice shard along its flight). */
export function fxTurned(ctx: Ctx, set: FxSet, clip: string, at: number, x: number, y: number, ang: number, alpha = 1): boolean {
  const s = art.set(set);
  if (!s || !s.has(clip)) return false;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(ang);
  s.draw(ctx, SCREEN, clipId(set, clip), clip, 0, 0, 0, 0, { at, alpha });
  ctx.restore();
  return true;
}

/** The baked rock radii (px) of `boulder-s`, `-m` and `-l`. */
const ROCK_PX = { s: 6.5, m: 9, l: 12 } as const;

/** Which baked rock fits a radius in px. */
export function rockSize(rPx: number): 's' | 'm' | 'l' {
  return rPx < (ROCK_PX.s + ROCK_PX.m) / 2 ? 's' : rPx < (ROCK_PX.m + ROCK_PX.l) / 2 ? 'm' : 'l';
}

/**
 * A rock, held or in flight, from the effects library: the baked size nearest its radius, turned by `spin` (one
 * clip loop per turn), a lava rock glowing through its cracks; anything else riding it glows round it.
 */
export function drawRockArt(ctx: Ctx, px: number, py: number, rPx: number, payload: Element | null, spin: number): boolean {
  const clip = `boulder-${payload === 'lava' ? 'lava-' : ''}${rockSize(rPx)}`;
  const at = (spin / (Math.PI * 2)) * fxSeconds('fx-projectile', clip);
  return fxAt(ctx, 'fx-projectile', clip, at, px, py);
}

/** Puffs a second each spray throws (phones fewer: `sprayPuffRate`). */
export const SPRAY_PUFFS_PER_S = 70;
let sprayPuffRate = SPRAY_PUFFS_PER_S;
let puffSeed = 7;
function puffRnd(): number {
  puffSeed = (puffSeed * 1664525 + 1013904223) >>> 0;
  return puffSeed / 4294967296;
}

/** Phones throw fewer puffs and keep fewer alive. */
export function setSprayBudget(touch: boolean): void {
  sprayPuffRate = touch ? SPRAY_PUFFS_PER_S * 0.45 : SPRAY_PUFFS_PER_S;
  sprayPuffs.limit = touch ? 64 : sprayPuffs.size;
}

/**
 * A spray's puffs (sprayCone.ts): each leaves the staff `z` units up just past the caster and lands on the ground
 * inside the cone (within `half` of `ang`, more toward the far part, out to `range`) as its clip ends, so the puffs
 * reach exactly as far as the spray hits. Elements take turns. False when the puffs have not landed yet.
 */
export function emitSprayPuffs(x: number, y: number, z: number, ang: number, half: number, range: number, el: readonly Element[], dt: number): boolean {
  if (el.length === 0 || !fxHas('fx-spray', `spray-${fxElement(el[0]!)}`)) return false;
  let n = sprayPuffRate * dt;
  const out = 0.3;
  for (let k = 0; n > 0; k++, n--) {
    if (n < 1 && puffRnd() > n) break;
    const e = fxElement(el[k % el.length]!);
    const clip = puffRnd() < 0.5 ? `spray-${e}` : `spray-${e}-b`;
    const life = fxSeconds('fx-spray', clip);
    if (life <= 0) continue;
    const a = ang + (puffRnd() * 2 - 1) * half;
    const d = Math.max(0.1, range * (0.3 + 0.7 * Math.sqrt(puffRnd())) - out);
    const c = Math.cos(a), s = Math.sin(a);
    sprayPuffs.spawn('fx-spray', clip, x + c * out, y + s * out, z, 0, 1, puffRnd() < 0.5, (c * d) / life, (s * d) / life, -z / life);
  }
  return true;
}

// ------------------------------------------------------------------ one-shot bursts

interface Shot { active: boolean; set: FxSet; clip: string; x: number; y: number; z: number; vx: number; vy: number; vz: number; t: number; dur: number; alpha: number; flip: boolean }

const SHOTS = 72;
/** The full pool (desktop); phones set a lower `limit`. */
export const FX_SHOTS_MAX = SHOTS;

/**
 * One-shot effect sprites in world space (an impact, a blast, a wall breaking, a spray's puff in flight): a fixed
 * pool; a full pool reuses the slot furthest through its clip. A negative start time is a delay; a shot given a
 * velocity (world units and height per second) flies while it plays.
 */
export class FxShots {
  private readonly pool: Shot[] = [];
  /** How many slots are in use at most (fewer on phones). */
  limit: number;

  constructor(readonly size = SHOTS) {
    this.limit = size;
    for (let i = 0; i < size; i++) this.pool.push({ active: false, set: 'fx-burst', clip: '', x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, t: 0, dur: 1, alpha: 1, flip: false });
  }

  clear(): void {
    for (const s of this.pool) s.active = false;
  }

  /** Start a clip at world (x, y), `z` units up, after `delay` seconds; false when the art is not there. */
  spawn(set: FxSet, clip: string, x: number, y: number, z = 0, delay = 0, alpha = 1, flip = false, vx = 0, vy = 0, vz = 0): boolean {
    const dur = fxSeconds(set, clip);
    if (dur <= 0) return false;
    let slot: Shot | null = null;
    const n = Math.min(this.limit, this.size);
    for (let i = 0; i < n; i++) if (!this.pool[i]!.active) { slot = this.pool[i]!; break; }
    if (!slot) {
      slot = this.pool[0]!;
      for (let i = 0; i < n; i++) { const s = this.pool[i]!; if (s.t / s.dur > slot.t / slot.dur) slot = s; }
    }
    slot.active = true; slot.set = set; slot.clip = clip; slot.x = x; slot.y = y; slot.z = z;
    slot.vx = vx; slot.vy = vy; slot.vz = vz;
    slot.t = -delay; slot.dur = dur; slot.alpha = alpha; slot.flip = flip;
    return true;
  }

  step(dt: number): void {
    for (const s of this.pool) {
      if (!s.active) continue;
      s.t += dt;
      if (s.t > 0) {
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        s.z = Math.max(0, s.z + s.vz * dt);
      }
      if (s.t >= s.dur) s.active = false;
    }
  }

  draw(ctx: Ctx, cam: Camera): void {
    for (const s of this.pool) {
      if (!s.active || s.t < 0) continue;
      fxAt(ctx, s.set, s.clip, s.t, cam.sx(s.x), cam.sy(s.y) - s.z * UNIT, s.alpha, s.flip);
    }
  }
}

/** Spray puffs in flight (emitSprayPuffs): their own pool, so a long spray never crowds out impacts and blasts. */
export const sprayPuffs = new FxShots(160);
